import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { FormProvider, useForm } from 'react-hook-form';

import { getRequestErrorMessage, pushNotify } from '../../../../../../components/Notifications';
import { useLazyDownloadReportQuery } from '../../../../../../redux/services/MerchantApi';
import { useEscapeKey } from '../../../../../../utils/hooks/useEscapeKey';
import { saveBlob } from '../../../../../../utils/functions/saveBlob';
import { Selector, TItem } from './SelectType';
import styles from './CreateReport.module.css';

interface ReportInputs {
  type: 'deposit' | 'withdrawal';
  status: string;
  date_from: string;
  date_to: string;
}

const emptyForm: ReportInputs = { type: 'deposit', status: 'all', date_from: '', date_to: '' };

const types: TItem<'deposit' | 'withdrawal'>[] = [
  { id: 'deposit', name: 'Пополнения' },
  { id: 'withdrawal', name: 'Выплаты' },
];
const depositStatuses: TItem<'all' | 'success' | 'failure' | 'idle' | 'nf'>[] = [
  { id: 'all', name: 'Все' },
  { id: 'success', name: 'Успешно завершённые' },
  { id: 'failure', name: 'Отменённые и просроченные' },
  { id: 'idle', name: 'В работе' },
  { id: 'nf', name: 'Реквизит не найден' },
];
const withdrawalStatuses: TItem<'all' | 'success' | 'failure' | 'in_progress' | 'problem'>[] = [
  { id: 'all', name: 'Все' },
  { id: 'success', name: 'Успешно завершённые' },
  { id: 'failure', name: 'Отменённые и просроченные' },
  { id: 'in_progress', name: 'В работе' },
  { id: 'problem', name: 'В поддержке' },
];

export const CreateReport: React.FC<{ open: boolean; onClose: () => void }> = ({ open, onClose }) => {
  const [triggerDownload, { isFetching }] = useLazyDownloadReportQuery();
  const [rangeError, setRangeError] = useState<string | null>(null);
  const methods = useForm<ReportInputs>({ mode: 'onSubmit', defaultValues: emptyForm });
  const { register, handleSubmit, watch, setValue, reset } = methods;
  const selectedType = watch('type');

  useEffect(() => {
    if (!open) {
      reset(emptyForm);
      setRangeError(null);
    }
  }, [open, reset]);
  useEscapeKey(open && !isFetching, onClose);

  const onSubmit = async (data: ReportInputs) => {
    if (data.date_from && data.date_to && data.date_from > data.date_to) {
      setRangeError('Дата «с» не может быть позже даты «по».');
      return;
    }
    setRangeError(null);
    try {
      // ждём именно этот запрос: иначе эффект по data скачивал предыдущий файл при повторной выгрузке
      const blob = await triggerDownload({
        type: data.type,
        status: data.status || 'all',
        date_from: data.date_from || undefined,
        date_to: data.date_to || undefined,
      }).unwrap();
      const ts = new Date().toISOString().replace(/[-:]/g, '').split('.')[0];
      saveBlob(blob, `${data.type === 'deposit' ? 'Deposits' : 'Withdrawals'}_${ts}.xlsx`);
      pushNotify('Отчёт скачан', 'Готово');
      onClose();
    } catch (error) {
      pushNotify(getRequestErrorMessage(error, 'Не удалось получить отчёт'), 'Ошибка');
    }
  };

  if (!open) return null;

  return createPortal(
    <FormProvider {...methods}>
      <div className={styles.backdrop} onClick={isFetching ? undefined : onClose} />
      <div className={styles.modal} role="dialog" aria-modal="true" aria-labelledby="create-report-title" aria-busy={isFetching}>
        <div className={styles.header}>
          <h2 id="create-report-title" className={styles.title}>Скачать отчёт</h2>
          <button type="button" aria-label="Закрыть" className={styles.closeBtn} onClick={onClose} disabled={isFetching}>×</button>
        </div>
        <form onSubmit={handleSubmit(onSubmit)} className={styles.form}>
          <Selector
            title="Тип отчёта"
            items={types}
            value={watch('type')}
            onChange={(v) => { setValue('type', v); setValue('status', 'all'); }}
          />
          <Selector
            title="Статус"
            items={selectedType === 'deposit' ? depositStatuses : withdrawalStatuses}
            value={watch('status')}
            onChange={(v) => setValue('status', v)}
          />
          <div className={styles.field}>
            <label className={styles.label} htmlFor="report-date-from">Дата с</label>
            <input id="report-date-from" type="date" {...register('date_from')} className={styles.input} />
          </div>
          <div className={styles.field}>
            <label className={styles.label} htmlFor="report-date-to">Дата по</label>
            <input id="report-date-to" type="date" {...register('date_to')} className={styles.input} />
          </div>
          {rangeError && <p role="alert" className={styles.label}>{rangeError}</p>}
          <button type="submit" className={styles.button} disabled={isFetching || !watch('date_from') || !watch('date_to')}>
            {isFetching ? 'Готовим файл…' : 'Скачать'}
          </button>
        </form>
      </div>
    </FormProvider>,
    document.body,
  );
};
